import { Component } from '@theme/component';

/**
 * VAT / invoice capture for the cart drawer.
 *
 * Talks to the Paint Factory app host directly (WISHLIST_APP_HOST), passing the
 * shop's .myshopify.com domain as a `?shop=` query parameter (window.themeSettings.shopDomain).
 *
 * Endpoints:
 * - GET  /customers/vat-info?email=...   → look up an existing customer's VAT number
 * - POST /vat/validation { vatNumber }   → validate a VAT number
 * - POST /customers/store { email, vatNumber } → create/update the customer
 *
 * On success the VAT number and invoice email are written to the cart attributes
 * (`vat_number`, `invoice_email`) so they carry through to the order at checkout.
 *
 * @typedef {{
 *   number: HTMLInputElement,
 *   submit: HTMLButtonElement,
 *   message: HTMLElement,
 *   fields?: HTMLElement,
 *   toggle?: HTMLInputElement,
 *   email?: HTMLInputElement,
 * }} CartVatRefs
 *
 * @typedef {{
 *   shopDomain?: string,
 * }} ThemeSettings
 *
 * @extends {Component<CartVatRefs>}
 */

const VAT_APP_HOST = 'https://paint-factory.apps.esign.eu';

class CartVatComponent extends Component {
  requiredRefs = ['number', 'submit', 'message'];

  #appliedPrefill = false;

  connectedCallback() {
    super.connectedCallback();
    this.#applyPrefilledVat();
  }

  /**
   * For logged-in customers with a stored VAT number, silently persist it to the
   * cart attributes so the invoice details flow to checkout without extra clicks.
   */
  #applyPrefilledVat() {
    if (this.dataset.loggedIn !== 'true') return;
    if (this.#appliedPrefill) return;

    const vatNumber = this.refs.number?.value.trim();
    if (!vatNumber) return;

    this.#appliedPrefill = true;
    this.#updateCartAttributes(vatNumber, this.dataset.customerEmail ?? '');
  }

  /**
   * Shows/hides the invoice fields for logged-out customers.
   * @param {Event} event
   */
  toggleInvoice = (event) => {
    const checkbox = event.target;
    const { fields } = this.refs;
    if (!fields || !(checkbox instanceof HTMLInputElement)) return;

    fields.classList.toggle('hidden', !checkbox.checked);

    if (!checkbox.checked) {
      this.#clearMessage();
      this.#updateCartAttributes('', '');
    }
  };

  /**
   * Looks up an existing VAT number when a logged-out customer enters their email.
   */
  lookupVat = async () => {
    const email = this.refs.email?.value.trim();
    if (!email) return;

    const url = this.#buildUrl('/customers/vat-info', { email });
    if (!url) return;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const data = await response.json().catch(() => null);
      const existingVat = this.#extractVatNumber(data);
      if (existingVat && this.refs.number && !this.refs.number.value.trim()) {
        this.refs.number.value = existingVat;
      }
    } catch (error) {
      console.error('[cart-vat] VAT lookup failed:', error);
    }
  };

  /**
   * Validates the VAT number, stores the customer, and persists the cart attributes.
   */
  save = async () => {
    const { number, submit } = this.refs;
    const vatNumber = number.value.trim();
    const email = this.#currentEmail();

    if (!vatNumber) {
      this.#showMessage(Theme?.translations?.vat_number_required, 'error');
      return;
    }

    if (!this.dataset.loggedIn && !email) {
      this.#showMessage(Theme?.translations?.vat_email_required, 'error');
      return;
    }

    this.#setLoading(submit, true);
    this.#showLoading(Theme?.translations?.vat_validating);

    try {
      const isValid = await this.#validateVat(vatNumber);
      if (!isValid) {
        this.#showMessage(Theme?.translations?.vat_invalid, 'error');
        return;
      }

      this.#showLoading(Theme?.translations?.vat_saving);

      await this.#storeCustomer(email, vatNumber);

      await this.#updateCartAttributes(vatNumber, email);
      this.#showMessage(Theme?.translations?.vat_saved, 'success');
    } catch (error) {
      console.error('[cart-vat] Save failed:', error);
      this.#showMessage(Theme?.translations?.vat_error, 'error');
    } finally {
      this.#setLoading(submit, false);
    }
  };

  /**
   * @param {string} vatNumber
   * @returns {Promise<boolean>}
   */
  async #validateVat(vatNumber) {
    const url = this.#buildUrl('/vat/validation');
    if (!url) throw new Error('Missing app proxy base');

    const response = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ vatNumber }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json().catch(() => null);
    if (data == null) return true;
    return Boolean(data.valid ?? data.isValid ?? data.data?.valid ?? data.data?.isValid);
  }

  /**
   * @param {string} email
   * @param {string} vatNumber
   */
  async #storeCustomer(email, vatNumber) {
    const url = this.#buildUrl('/customers/store');
    if (!url) throw new Error('Missing app proxy base');

    const response = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, vatNumber }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  }

  /**
   * Persists the VAT number and invoice email to the cart attributes.
   * @param {string} vatNumber
   * @param {string} email
   */
  async #updateCartAttributes(vatNumber, email) {
    const updateUrl = Theme?.routes?.cart_update_url;
    if (!updateUrl) return;

    await fetch(`${updateUrl}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          vat_number: vatNumber,
          invoice_email: email,
        },
      }),
    }).catch((error) => console.error('[cart-vat] Cart attribute update failed:', error));
  }

  /**
   * @returns {string}
   */
  #currentEmail() {
    if (this.dataset.loggedIn === 'true') return this.dataset.customerEmail ?? '';
    return this.refs.email?.value.trim() ?? '';
  }

  /**
   * Extracts a VAT number from a variety of possible response shapes.
   * @param {any} data
   * @returns {string}
   */
  #extractVatNumber(data) {
    if (!data) return '';
    return (
      data.vatNumber ??
      data.vat_number ??
      data.companyVat ??
      data.company_vat ??
      data.data?.vatNumber ??
      data.data?.vat_number ??
      ''
    );
  }

  /**
   * Builds a request URL hitting the app host directly, mirroring the wishlist handling.
   * @param {string} path
   * @param {Record<string, string>} [params]
   * @returns {string}
   */
  #buildUrl(path, params = {}) {
    const settings = /** @type {ThemeSettings | undefined} */ (
      /** @type {Window & { themeSettings?: ThemeSettings }} */ (window).themeSettings
    );
    const query = new URLSearchParams(params);
    if (settings?.shopDomain) query.set('shop', settings.shopDomain);
    const queryString = query.toString();
    return `${VAT_APP_HOST}${path}${queryString ? `?${queryString}` : ''}`;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} loading
   */
  #setLoading(button, loading) {
    button.toggleAttribute('aria-busy', loading);
    button.disabled = loading;
  }

  /**
   * Shows a loading status with an inline spinner while the request is in flight.
   * @param {string | undefined} text
   */
  #showLoading(text) {
    const { message } = this.refs;
    message.textContent = '';

    const spinner = document.createElement('span');
    spinner.className = 'cart-vat__message-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.textContent = text ?? '';

    message.append(spinner, label);
    message.classList.remove('hidden', 'cart-vat__message--error', 'cart-vat__message--success');
    message.classList.add('cart-vat__message--loading');
  }

  /**
   * @param {string | undefined} text
   * @param {'error' | 'success'} type
   */
  #showMessage(text, type) {
    const { message } = this.refs;
    message.textContent = text ?? '';
    message.classList.remove(
      'hidden',
      'cart-vat__message--loading',
      'cart-vat__message--error',
      'cart-vat__message--success'
    );
    message.classList.add(`cart-vat__message--${type}`);
  }

  #clearMessage() {
    const { message } = this.refs;
    message.textContent = '';
    message.classList.add('hidden');
    message.classList.remove(
      'cart-vat__message--loading',
      'cart-vat__message--error',
      'cart-vat__message--success'
    );
  }
}

if (!customElements.get('cart-vat-component')) {
  customElements.define('cart-vat-component', CartVatComponent);
}
